import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Button, Table, Modal, Form, Input, message, Popconfirm, Alert, Row, Col, Tag, Divider } from 'antd';
import { KeyOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authAPI, adminAPI } from '../services/api';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [activationCodes, setActivationCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createUserModalVisible, setCreateUserModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCodeModalVisible, setNewCodeModalVisible] = useState(false);
  const [newCodeData, setNewCodeData] = useState(null);
  const [newUserModalVisible, setNewUserModalVisible] = useState(false);
  const [newUserData, setNewUserData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchUserInfo();
    if (isAdmin) {
      fetchActivationCodes();
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUserInfo = async () => {
    try {
      const response = await authAPI.getMe();
      setUserInfo(response.data);
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        fetchActivationCodes();
      }
    } catch (error) {
      message.error('获取用户信息失败');
      navigate('/login');
    }
  };

  const fetchActivationCodes = async () => {
    try {
      const response = await adminAPI.listActivationCodes();
      setActivationCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch activation codes:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.listUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateActivationCode = async (values) => {
    setLoading(true);
    try {
      const response = await adminAPI.createActivationCode(
        values.username,
        values.max_uses !== undefined ? values.max_uses : 1,
        values.expire_days !== undefined ? values.expire_days : 30
      );
      message.success('激活码创建成功');
      setNewCodeData(response.data);
      setCreateModalVisible(false);
      setNewCodeModalVisible(true);
      fetchActivationCodes();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (values) => {
    setLoading(true);
    try {
      const response = await adminAPI.createUser(values.username, values.role);
      message.success('用户创建成功');
      setNewUserData(response.data);
      setCreateUserModalVisible(false);
      setNewUserModalVisible(true);
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeCode = async (codeId) => {
    try {
      await adminAPI.revokeActivationCode(codeId);
      message.success('激活码已撤销');
      fetchActivationCodes();
    } catch (error) {
      message.error('撤销失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '激活码',
      dataIndex: 'activation_code',
      key: 'activation_code',
      width: 200,
      render: (code) => {
        if (!code) {
          return <Text type="secondary">无法显示</Text>;
        }
        return (
          <Space>
            <Text copyable={{ text: code }} style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {code}
            </Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(code);
                message.success('激活码已复制');
              }}
            />
          </Space>
        );
      },
    },
    {
      title: '绑定用户',
      dataIndex: 'bound_username',
      key: 'bound_username',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const colorMap = {
          'ACTIVE': 'green',
          'CONSUMED': 'orange',
          'EXPIRED': 'red',
          'REVOKED': 'default'
        };
        return <Text type={colorMap[status] || 'default'}>{status}</Text>;
      }
    },
    {
      title: '使用次数',
      key: 'usage',
      width: 120,
      render: (_, record) => {
        const display = record.display_max_uses || record.max_uses;
        return `${record.used_count}/${display}`;
      }
    },
    {
      title: '有效期',
      key: 'expires',
      width: 150,
      render: (_, record) => {
        if (record.display_expires) {
          return <Text type="secondary">{record.display_expires}</Text>;
        }
        return <Text type="success">永久有效</Text>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        record.status === 'ACTIVE' && (
          <Popconfirm
            title="确定要撤销此激活码吗？"
            onConfirm={() => handleRevokeCode(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger>撤销</Button>
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <div style={{ background: '#0d1117', color: '#e6edf3' }}>
      <Card 
        title="用户信息" 
        style={{ 
          marginBottom: 24,
          background: '#161b22',
          border: '1px solid #30363d',
          color: '#e6edf3'
        }}
        headStyle={{
          color: '#e6edf3',
          borderBottom: '1px solid #30363d'
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text><strong>用户名：</strong>{userInfo?.username}</Text>
          <Text><strong>角色：</strong>{userInfo?.is_admin ? '管理员' : '普通用户'}</Text>
          <Text><strong>账号状态：</strong>{userInfo?.enabled ? '正常' : '禁用'}</Text>

          {userInfo?.activation_code && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  您的激活码（可复制使用）
                </Text>
                <div style={{
                  background: '#21262d',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #30363d'
                }}>
                  <Space>
                    <Text strong style={{ fontSize: 18, fontFamily: 'monospace', letterSpacing: '2px' }}>
                      {userInfo.activation_code}
                    </Text>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(userInfo.activation_code);
                        message.success('激活码已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  </Space>
                  <div style={{ marginTop: 8 }}>
                    {userInfo.activation_code_is_unlimited ? (
                      <Tag color="green">无限次使用</Tag>
                    ) : (
                      <Tag color="blue">
                        剩余 {userInfo.activation_code_remaining || 0} 次
                      </Tag>
                    )}
                    {userInfo.activation_code_expires_at && (
                      <Tag color="orange">
                        {new Date(userInfo.activation_code_expires_at) > new Date() ?
                          `有效期至 ${new Date(userInfo.activation_code_expires_at).toLocaleDateString()}` :
                          '已过期'
                        }
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Space>
      </Card>

      {isAdmin && (
        <>
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card
                title="用户管理"
                extra={
                  <Button type="primary" onClick={() => setCreateUserModalVisible(true)}>
                    创建用户
                  </Button>
                }
              >
                <Table
                  columns={[
                    {
                      title: 'ID',
                      dataIndex: 'id',
                      key: 'id',
                      width: 80,
                    },
                    {
                      title: '用户名',
                      dataIndex: 'username',
                      key: 'username',
                    },
                    {
                      title: '角色',
                      dataIndex: 'role_name',
                      key: 'role_name',
                      render: (role) => role || '无角色',
                    },
                    {
                      title: '状态',
                      dataIndex: 'enabled',
                      key: 'enabled',
                      render: (enabled) => (
                        <Tag color={enabled ? 'green' : 'red'}>
                          {enabled ? '启用' : '禁用'}
                        </Tag>
                      ),
                    },
                    {
                      title: '创建时间',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      render: (text) => text ? new Date(text).toLocaleString() : '-',
                    },
                    {
                      title: '最后登录',
                      dataIndex: 'last_login_at',
                      key: 'last_login_at',
                      render: (text) => text ? new Date(text).toLocaleString() : '从未登录',
                    },
                  ]}
                  dataSource={users}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card
                title="激活码管理"
                extra={
                  <Button type="primary" icon={<KeyOutlined />} onClick={() => setCreateModalVisible(true)}>
                    生成激活码
                  </Button>
                }
              >
                <Table
                  columns={columns}
                  dataSource={activationCodes}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title="生成激活码"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleCreateActivationCode}
        >
          <Form.Item
            name="username"
            label="绑定用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="max_uses"
            label="最大使用次数"
            initialValue={1}
          >
            <Input type="number" min={1} />
          </Form.Item>

          <Form.Item
            name="expire_days"
            label="有效期（天）"
            initialValue={30}
          >
            <Input type="number" min={1} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              生成
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="创建用户"
        open={createUserModalVisible}
        onCancel={() => setCreateUserModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3位' }
            ]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Alert
            message="默认密码"
            description="新用户将使用默认密码 P!ssw0rdabc123"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              创建
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="✅ 激活码创建成功"
        open={newCodeModalVisible}
        onCancel={() => setNewCodeModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setNewCodeModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={500}
      >
        {newCodeData && (
          <div>
            <Alert
              message="请复制此激活码发送给用户"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <div style={{
              background: '#f5f5f5',
              padding: '24px',
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: 16
            }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                激活码
              </Text>
              <Title level={2} style={{
                fontFamily: 'monospace',
                letterSpacing: '4px',
                margin: 0,
                wordBreak: 'break-all'
              }}>
                {newCodeData.activation_code}
              </Title>
            </div>

            <div style={{ background: '#fffbe6', padding: '12px', borderRadius: '4px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text><strong>绑定用户：</strong>{newCodeData.username}</Text>
                <Text><strong>最大使用次数：</strong>{newCodeData.max_uses}</Text>
                <Text><strong>过期时间：</strong>{newCodeData.expires_at ? new Date(newCodeData.expires_at).toLocaleString() : '永不过期'}</Text>
              </Space>
            </div>

            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(newCodeData.activation_code);
                message.success('激活码已复制到剪贴板');
              }}
              style={{ marginTop: 16, padding: 0 }}
            >
              点击复制激活码
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
